// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VideoVendor {
  id: string;
  user_id: string;
  email: string;
  name: string;
  commission_rate?: number;
  created_at: string;
  membership_status: string;
  total_orders: number;
  total_revenue: number;
}

interface VendorDetailsModalProps {
  vendor: VideoVendor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVendorUpdate: (updatedVendor: VideoVendor) => void;
}

const VendorDetailsModal: React.FC<VendorDetailsModalProps> = ({
  vendor,
  open,
  onOpenChange,
  onVendorUpdate,
}) => {
  const [commissionRate, setCommissionRate] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  React.useEffect(() => {
    if (vendor) {
      setCommissionRate(vendor.commission_rate?.toString() || '');
    }
  }, [vendor]);

  const handleUpdateCommission = async () => {
    if (!vendor) return;

    try {
      setIsUpdating(true);
      const rate = parseFloat(commissionRate);
      
      if (isNaN(rate) || rate < 0 || rate > 100) {
        toast.error('Commission rate must be between 0 and 100');
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ commission_rate: rate })
        .eq('id', vendor.id);

      if (error) throw error;

      // Update the vendor object
      const updatedVendor = { ...vendor, commission_rate: rate };
      onVendorUpdate(updatedVendor);
      
      toast.success('Commission rate updated successfully');
    } catch (error) {
      console.error('Error updating commission rate:', error);
      toast.error('Failed to update commission rate');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!vendor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Vendor Details - {vendor.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Vendor Info */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <p className="font-medium">{vendor.name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="font-medium">{vendor.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Badge variant={vendor.membership_status === 'active' ? 'default' : 'secondary'}>
                    {vendor.membership_status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Joined</Label>
                  <p className="font-medium">{new Date(vendor.created_at).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Total Orders</Label>
                  <p className="text-2xl font-bold">{vendor.total_orders}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Total Revenue</Label>
                  <p className="text-2xl font-bold">${vendor.total_revenue.toFixed(2)}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Commission Rate Control */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Commission Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end space-x-2">
                <div className="flex-1">
                  <Label htmlFor="commission">Commission Rate (%)</Label>
                  <Input
                    id="commission"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(e.target.value)}
                    placeholder="Enter commission rate"
                  />
                </div>
                <Button 
                  onClick={handleUpdateCommission}
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Updating...' : 'Update'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Current rate: {vendor.commission_rate ? `${vendor.commission_rate}%` : 'Not set'}
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VendorDetailsModal;